import {
  enableMysiteRedesignFeature,
  enablePORedesignFeature,
  i18nTranslate,
} from 'src/utils'
import {
  APPConfig,
  deleteFromLocalStorage,
  convertToBoolean,
  getLocalStorage,
  getSessionStorage,
  IS_BROWSER,
  localDomain,
} from 'config/appConfig'
import { customerContainer, sessionContainer } from 'src/models'
import {
  setLocalStorage,
  checkMysiteOrPersonalOffer,
  getShoppingContext,
  getLiveEventStatus,
} from 'src/utils'
import { toastState } from 'src/views/components'
import { getLocaleCodeFromUrl } from 'src/utils/localeUtils'
import { pageNames } from 'src/routes/pathParams'
import {
  getMysiteSponsor,
  isEquinoxMysite,
  isEquinoxPOSite,
  getPoSponsorId,
  getPersonalOfferPitchId,
} from './mySiteUtils'
import { PO_LANDING_URL_KEY } from 'src/utils/constant'

export function setCallbackLocaleInLocalStorage() {
  setLocalStorage('login_callback_locale', getLocaleCodeFromUrl())
  setLocalStorage(
    'login_callback_store_id',
    APPConfig?.getAppConfig()?.storeId || ''
  )
}

/**
 * Initiates sign in flow with redirect to Okta authorization endpoint.
 *
 * @param {string} isUrlReturn - The URL to redirect to after successful sign in.
 */
export async function signInwithRedirect(isUrlReturn) {
  const enableLocaleInOktaCallback = convertToBoolean(
    APPConfig?.getAppConfig()?.enableLocaleInOktaCallback || false
  )
  const response = await sessionContainer.oktaAuthorize()
  let oktaAuthorizeUrl = response?.oktaAuthorizeUrl || ''

  let oktaDomain = oktaAuthorizeUrl.substring(
    0,
    oktaAuthorizeUrl.indexOf('.com')
  )
  oktaDomain = oktaDomain.concat('.com')
  try {
    localStorage.setItem('oktaDomain', oktaDomain)
  } catch (error) {
    console.error('Failed to set item in localStorage:', error)
  }

  const codeVerifier = response?.codeVerifier || ''
  try {
    localStorage.setItem('codeVerifier', codeVerifier)
  } catch (error) {
    console.error('Failed to set item in localStorage:', error)
  }

  // Added support to use the current domain
  const oktaUrl = new URL(oktaAuthorizeUrl)
  let redirectUri = oktaUrl?.searchParams?.get('redirect_uri')
  const currentLocale = getLocaleCodeFromUrl()?.toLowerCase()
  const curLocaleFromUrl = currentLocale?.split('_')
  let locale = ''

  if (curLocaleFromUrl?.[0]) {
    locale = curLocaleFromUrl[1] + '/' + curLocaleFromUrl[0]
  }
  // console.log('redirectUri from api', redirectUri)

  if (document.domain != localDomain) {
    if (redirectUri?.includes('https://')) {
      redirectUri = redirectUri.replace('https://', '')
      const slashPos = redirectUri?.indexOf('/')
      redirectUri = redirectUri.substring(slashPos)
    }
    // const oktaRedirectUrl =
    //   `https://${document.domain}` +
    //   (enableLocaleInOktaCallback ? redirectUri : pageNames.loginCallback)
    const shouldIncludeLocale =
      locale && !redirectUri?.includes(locale) && enableLocaleInOktaCallback
    // Appending locale when redirect URI missing locale in URL
    const oktaRedirectUrl = `https://${document.domain}${
      shouldIncludeLocale ? '/' + locale : ''
    }${redirectUri}`

    // console.log(
    //   'redirectUrl,oktaRedirectUrl after changes',
    //   redirectUri,
    //   oktaRedirectUrl
    // )
    oktaUrl?.searchParams?.set('redirect_uri', oktaRedirectUrl)
    oktaAuthorizeUrl = oktaUrl.toString()
  }

  // console.log('oktaAuthorizeUrl ', oktaAuthorizeUrl)

  if (response.statusCode === 200 && typeof window !== 'undefined') {
    setCallbackLocaleInLocalStorage()
    // const shoppingContext = getLocalStorage('shoppingContext') || {}
    const shoppingContext = getShoppingContext()
    if (Object.keys(shoppingContext).length > 0) {
      /**
       * @info
       * remove mysiteCheckout from local storage if it's already there
       */
      if (getLocalStorage('mysiteCheckout')) {
        localStorage.removeItem('mysiteCheckout')
      }
      if (isUrlReturn === 'mysiteCheckout') {
        setLocalStorage('mysiteCheckout', 'mysiteCheckout')
      }
    }
    if (isUrlReturn === true) {
      return oktaAuthorizeUrl
    } else {
      window.location.href = oktaAuthorizeUrl
    }
  } else {
    deleteFromLocalStorage('isExpressPDPLogin')
    toastState.setToastMessage(
      i18nTranslate('signIn.authorizeFailMessage', 'Authorize call failure'),
      false
    )
    setTimeout(() => {
      window.location.href = window.location.origin
    }, 3000)
  }
}

/**
 * Publishes a 'nuskin-account-logout' event when the user logs out.
 * This allows other parts of the app to respond to the logout event.
 *
 * Checks if it is a native mobile app first, and only publishes the event
 * if it is a web app.
 */
export async function publishLogoutEvent() {
  const enableLiveEvent = getLiveEventStatus()
  const enableCrossTabLogout = convertToBoolean(
    APPConfig?.getAppConfig()?.enableCrossTabLogout
  )
  // Fix CX121-4987
  const isNativeMobileApp =
    window?.isNativeApp == true || window?.isVeraApp == true
  if (!isNativeMobileApp) {
    if (enableCrossTabLogout) {
      const { publishCrossTabLogout } = await import('./crossTabLogout')
      publishCrossTabLogout()
    } else {
      const { events } = await import('@nuskin/ns-util')
      if (events) {
        events?.publish?.('nuskin-account-logout')
        if (enableLiveEvent === 'true') {
          events?.publish?.('signout')
        }
      }
    }
  }
}

/**
 * Navigates to a new page after a timeout.
 *
 * @param {Object} options - Options for navigation
 * @param {number} [options.timeout=2000] - Timeout before navigating in ms
 * @param {string} options.url - URL to navigate to
 */
async function pageNavigate(options) {
  const { timeout = 2000, url } = options || {}

  setTimeout(async () => {
    await publishLogoutEvent()
    if (url) {
      window.location.href = url
    }
  }, timeout)
}

function getPitchIdFromPersonalOfferSession() {
  const personalOffer = getSessionStorage?.('personalOffer') || {}
  return personalOffer?.offerId || personalOffer?.shareLink?.id || ''
}

function buildPOLandingUrl({ country = '', language = '', sponsorId = '' }) {
  if (!sponsorId) return ''
  const pitchId =
    getPersonalOfferPitchId() || getPitchIdFromPersonalOfferSession()
  const pitchIdParam = pitchId ? `?pitchId=${pitchId}` : ''
  return `${window.location.origin}/${country?.toLowerCase()}/${language?.toLowerCase()}/personal-offer/${sponsorId}${pitchIdParam}`
}

function getPOSponsorFromPathname() {
  const pathname = window?.location?.pathname || ''
  const sponsorId = pathname
    ?.split('/personal-offer/')?.[1]
    ?.split('/')?.[0]
    ?.split('?')?.[0]
    ?.trim()
  return sponsorId || ''
}

function getPOSponsorIdForLogout() {
  const poSponsorFromPathname = getPOSponsorFromPathname()
  if (poSponsorFromPathname) {
    return poSponsorFromPathname
  }

  if (isEquinoxPOSite()) {
    return getPoSponsorId()
  }
  // Keep non-PO URL signout scoped to PO session data only.
  return getSessionStorage?.('sponsor') || ''
}

function isPOLogoutContext(shoppingContextValue = '') {
  if (!enablePORedesignFeature()) {
    return false
  }

  if (isEquinoxPOSite()) {
    return true
  }

  if (shoppingContextValue === 'personal_offer') {
    return true
  }

  return !!getPitchIdFromPersonalOfferSession()
}

function setPOLogoutLandingUrl(country = '', language = '') {
  if (!IS_BROWSER || !enablePORedesignFeature()) {
    return
  }

  const shoppingContext = getShoppingContext?.() || {}
  const shoppingContextValue = shoppingContext?.context || ''
  if (!isPOLogoutContext(shoppingContextValue)) {
    return
  }

  const poSponsor = getPOSponsorIdForLogout()
  const poLandingUrl = buildPOLandingUrl({
    country,
    language,
    sponsorId: poSponsor,
  })

  if (poLandingUrl) {
    sessionStorage.setItem(PO_LANDING_URL_KEY, poLandingUrl)
  }
}

function consumePOLogoutLandingUrl() {
  if (!IS_BROWSER) {
    return ''
  }
  const poLogoutLandingUrl = sessionStorage.getItem(PO_LANDING_URL_KEY) || ''
  if (poLogoutLandingUrl) {
    sessionStorage.removeItem(PO_LANDING_URL_KEY)
  }
  // Only accept values that look like a PO landing URL. Guards against
  // stale/foreign values ever being interpreted as a redirect target.
  if (!poLogoutLandingUrl.includes('/personal-offer/')) {
    return ''
  }
  return poLogoutLandingUrl
}

/**
 * Logs the user out and navigates to the homepage.
 * Gets the current locale from the URL.
 * Checks if it's a mySite/personal offer logout, and if so
 * redirects to the mySite logout redirect URI.
 * Otherwise redirects to the homepage with the current locale.
 */
export async function customerLogout() {
  await customerContainer.logout()
  customerContainer.isAccountTerminated = false

  // const curLocale = getLocalStorage('defaultLocale').replace(/"/g, '')
  let curLocale = getLocaleCodeFromUrl({
    at: 'pathParam',
    isReverseType: true,
  })

  if (!curLocale) {
    // existing flow untouched - when locale is available in url
    curLocale =
      getLocalStorage?.('login_callback_locale')?.replace('-', '_') || ''
  }
  const [language = '', country = ''] = curLocale.includes('_')
    ? curLocale.split('_')
    : curLocale.split('-')
  if (typeof window !== 'undefined') {
    const pathname =
      typeof window?.location?.pathname === 'string'
        ? window.location.pathname
        : ''
    // Consume the pre-signout cached PO landing URL for any post-signout
    // landing (logout callback, locale home fallthrough, etc.). This key is
    // ONLY set by setPOLogoutLandingUrl in signOutWithRedirect, which
    // requires enablePORedesignFeature() AND a live PO context, so this
    // consumption is inherently scoped to PO signout flows and cannot leak
    // to mysite/regular storefront signouts. We skip when already on a
    // /personal-offer/ path to avoid same-URL redirect loops on the PO
    // landing page itself.
    if (enablePORedesignFeature() && !pathname.includes('/personal-offer/')) {
      const poLogoutLandingUrl = consumePOLogoutLandingUrl()
      if (poLogoutLandingUrl) {
        pageNavigate({
          url: poLogoutLandingUrl,
        })
        return
      }
    }

    // condition to check mysite / personal offer logout - CX121-4221
    // const shoppingContext = getLocalStorage('shoppingContext') || {}
    const shoppingContext = getShoppingContext?.() || {}
    const shoppingContextValue = shoppingContext?.context || ''
    const shouldRedirectToPOLanding = isPOLogoutContext(shoppingContextValue)
    const contextValue =
      isEquinoxMysite() || isEquinoxPOSite() ? '' : checkMysiteOrPersonalOffer()
    if (shouldRedirectToPOLanding) {
      const poSponsor = getPOSponsorIdForLogout()
      const poLandingUrl = buildPOLandingUrl({
        country,
        language,
        sponsorId: poSponsor,
      })
      if (poLandingUrl) {
        pageNavigate({
          url: poLandingUrl,
        })
      } else {
        pageNavigate({
          url: `${window.location.origin}/${country}/${language}`.toLowerCase(),
        })
      }
    } else if (
      (contextValue == 'personal_offer' && !enablePORedesignFeature()) ||
      contextValue == 'storefront'
    ) {
      const mySiteLogoutRedirectURI =
        APPConfig?.getAppConfig()?.MYSITE_REDIRECT || ''
      let mySiteDomain
      let urlDetails = sessionStorage.getItem('storefront') || '{}'
      if (urlDetails) {
        urlDetails = JSON.parse(urlDetails) || {}
      }
      if (
        urlDetails?.storefront &&
        Object.keys(urlDetails?.storefront).length > 0
      ) {
        mySiteDomain = Object.keys(urlDetails?.storefront)
        mySiteDomain = mySiteDomain?.[0] || ''
      }
      pageNavigate({
        url: mySiteLogoutRedirectURI.replace('<%domain%>', mySiteDomain),
        timeout: 3000,
      })
    } else if (isEquinoxMysite()) {
      const mysiteSponsor = getMysiteSponsor()
      if (mysiteSponsor && enableMysiteRedesignFeature()) {
        pageNavigate({
          url: `${window.location.origin}/${country}/${language}/mysite/${mysiteSponsor}`.toLowerCase(),
        })
      }
    } else if (isEquinoxPOSite()) {
      const poSponsor = getPoSponsorId()
      const poLandingUrl = buildPOLandingUrl({
        country,
        language,
        sponsorId: poSponsor,
      })
      if (poLandingUrl && enablePORedesignFeature()) {
        pageNavigate({
          url: poLandingUrl,
        })
      }
    } else {
      pageNavigate({
        url: `${window.location.origin}/${country}/${language}`.toLowerCase(),
      })
    }
  }
}

function buildOktaLogoutUrl(
  oktaLogoutUri,
  postOktaLogoutRedirectURI,
  restrictLocaleToOktaSignout,
  country,
  language
) {
  let logoutCallbackUrl = ''
  if (convertToBoolean(restrictLocaleToOktaSignout)) {
    logoutCallbackUrl = postOktaLogoutRedirectURI.replace(
      '/<%LOCALE_CODE%>',
      ''
    )
  } else {
    logoutCallbackUrl = `${postOktaLogoutRedirectURI.replace(
      '<%LOCALE_CODE%>',
      `${country}/${language}`.toLowerCase()
    )}`
  }
  return `${oktaLogoutUri}id_token_hint=${localStorage?.getItem(
    'idToken'
  )}&post_logout_redirect_uri=https://${document?.domain}${logoutCallbackUrl}`
}

function getRedirectLocaleFromConfig(curLocale) {
  return curLocale.includes('_') ? curLocale.split('_') : curLocale.split('-')
}

/**
 * Logs the user out and navigates to the homepage.
 * Gets the current locale from the URL.
 * Checks if it's a mySite/personal offer logout, and if so
 * redirects to the mySite logout redirect URI.
 * Otherwise redirects to the homepage with the current locale.
 */
export async function signOutWithRedirect() {
  const {
    postOktaLogoutRedirectURI,
    defaultLocale,
    restrictLocaleToOktaSignout,
  } = APPConfig.getAppConfig()

  const curLocaleFromUrl = getLocaleCodeFromUrl({
    isReverseType: true,
  })

  const curLocale = defaultLocale || curLocaleFromUrl

  setCallbackLocaleInLocalStorage()

  const [language, country] = getRedirectLocaleFromConfig(curLocale)
  setPOLogoutLandingUrl(country, language)

  if (!IS_BROWSER || !localStorage.getItem('idToken')) {
    await handleCSRLogout(country, language)
    return
  }

  const isPOPath =
    typeof window?.location?.pathname === 'string' &&
    window.location.pathname.includes('/personal-offer/')
  if (enablePORedesignFeature() && isPOPath) {
    await customerLogout()
    return
  }

  const contextValue =
    isEquinoxMysite() || isEquinoxPOSite() ? '' : checkMysiteOrPersonalOffer()

  if (contextValue) {
    const oktaLogoutUri = APPConfig.getAppConfig().oktaLogoutUri
    const composedLogoutURL = buildOktaLogoutUrl(
      oktaLogoutUri,
      postOktaLogoutRedirectURI,
      restrictLocaleToOktaSignout,
      country,
      language
    )
    window.location.replace(composedLogoutURL)
  } else {
    await customerLogout()
  }
}

async function handleCSRLogout(country, language) {
  /**
   * @info
   * below logout functionaliy will happen when the user login from CSR site
   * added timeout inorder to display toast message before redirection
   */
  await customerContainer.csrlogout()

  await publishLogoutEvent()

  if (IS_BROWSER) {
    setTimeout(() => {
      window.location.href =
        `${window.location.origin}/${country}/${language}`.toLowerCase()
    }, 2000)
  }
}

export async function redirectToLoginCallbackWithLocale() {
  let isPageRedirecting = false
  try {
    if (typeof window !== 'undefined') {
      const currentLocale = getLocaleCodeFromUrl()
      const isLoginCallback = window.location.pathname.includes(
        pageNames.loginCallback
      )
      const loginCallbackLocale =
        getLocalStorage?.('login_callback_locale')?.replace('-', '_') || ''
      if (!currentLocale && isLoginCallback && loginCallbackLocale) {
        const [languageFromLocalStorage, countryFromLocalStorage] =
          loginCallbackLocale.split('_')
        const currentUrl = window.location.href
        const url = new URL(currentUrl)
        const searchParams = url.searchParams
        const language = languageFromLocalStorage?.toLowerCase() || ''
        const country = countryFromLocalStorage?.toLowerCase() || ''
        if (language && country) {
          const commonBasePath = `${window.location.origin}/${country}/${language}`
          let newUrl = `${commonBasePath}${pageNames.loginCallback}?${searchParams.toString()}`
          isPageRedirecting = true
          window.location.href = newUrl
        }
      }
    }
  } catch (error) {
    console.log('Error in redirectToLoginCallbackWithLocale:', error)
    return isPageRedirecting
  }
  return isPageRedirecting
}

export function isUserAuthenticated() {
  const value = getLocalStorage('IsAuthenticated')
  return value === true || value === 'true'
}

// Re-entry after login, no pending okta (MDIGI-4331)
export function isReEntryAfterLogin() {
  const hasPendingOktaAuth = !!getLocalStorage('oktaUrlState')
  return isUserAuthenticated() && !hasPendingOktaAuth
}

// Previous page, never the callback — sanitized against open-redirect
export function getReEntryRedirectUrl() {
  const stripQuotes = url => (url || '').replaceAll('"', '')
  const isSafeRelativeUrl = url => {
    if (!url || typeof url !== 'string') return false
    if (!url.startsWith('/')) return false
    if (url.startsWith('//')) return false
    return true
  }
  const sanitize = url => {
    const stripped = stripQuotes(url)
    return isSafeRelativeUrl(stripped) ? stripped : null
  }
  const isCallbackUrl = url => !!url && url.includes(pageNames.loginCallback)
  const prevPageURL = sanitize(getLocalStorage('prevPageUrl'))
  const curPageURL = sanitize(getLocalStorage('curPageUrl'))

  if (prevPageURL && !isCallbackUrl(prevPageURL)) return prevPageURL
  if (curPageURL && !isCallbackUrl(curPageURL)) return curPageURL
  return pageNames.home
}
